import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatGBP, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Search, X, Edit3, Trash2, Calendar as CalIcon, Upload, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const EMPTY = {
  name: "", address: "", postcode: "", phone: "", email: "",
  price: 0, cleaning_type: "exterior", notes: "", access_notes: "",
  gate_code: "", dog_warning: false, preferred_day: "", frequency: "monthly",
  custom_days: null, lat: null, lng: null, active: true,
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...EMPTY, ...c });
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price) || 0, custom_days: form.custom_days ? Number(form.custom_days) : null };
      if (editing === "new") {
        await api.post("/customers", payload);
        toast.success("Customer added");
      } else {
        await api.patch(`/customers/${editing}`, payload);
        toast.success("Customer updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer? Their job history will remain.")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
  };

  const scheduleNext = async (id) => {
    try {
      await api.post(`/jobs/generate-next/${id}`);
      toast.success("Next job scheduled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const filtered = items.filter((c) => {
    const s = q.toLowerCase();
    if (!s) return true;
    return [c.name, c.address, c.postcode, c.email, c.phone].some((f) => (f || "").toLowerCase().includes(s));
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight font-bold">Customers</h1>
          <div className="text-sm text-muted-foreground mt-1">{items.length} on your round</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted" data-testid="import-customers-button">
            <Upload className="h-4 w-4" /> Import CSV
          </button>
          <button onClick={openNew} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90" data-testid="add-customer-button">
            <Plus className="h-4 w-4" /> Add customer
          </button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, address, postcode…" className="w-full rounded-md border border-input pl-9 pr-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none" data-testid="customers-search" />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Postcode</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No customers yet. Add your first.</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} className="hover:bg-muted/40" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.address}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{c.postcode}</td>
                  <td className="px-4 py-3 capitalize">{(c.frequency || "").replace("_", " ")}</td>
                  <td className="px-4 py-3">{formatGBP(c.price)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{c.phone}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => scheduleNext(c.id)} data-testid={`schedule-next-${c.id}`}><CalIcon className="h-4 w-4 mr-1" />Schedule</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Edit3 className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => del(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add customer" : "Edit customer"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="customer-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="customer-name" />
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="customer-phone" />
            </div>
            <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="customer-address" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v.toUpperCase() })} testid="customer-postcode" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="customer-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Price £" type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="customer-price" />
              <div>
                <label className="text-sm font-medium">Frequency</label>
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="customer-frequency">
                  <option value="one_off">One-off</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom_days">Custom days</option>
                </select>
              </div>
              {form.frequency === "custom_days" && (
                <Field label="Every N days" type="number" value={form.custom_days || ""} onChange={(v) => setForm({ ...form, custom_days: v })} testid="customer-custom-days" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Cleaning type</label>
                <select value={form.cleaning_type} onChange={(e) => setForm({ ...form, cleaning_type: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="customer-cleaning-type">
                  <option value="exterior">Exterior</option>
                  <option value="interior">Interior</option>
                  <option value="both">Both</option>
                  <option value="frames">Frames & sills</option>
                  <option value="gutters">Gutters</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Preferred day</label>
                <select value={form.preferred_day} onChange={(e) => setForm({ ...form, preferred_day: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="customer-preferred-day">
                  <option value="">Any</option>
                  <option>Monday</option><option>Tuesday</option><option>Wednesday</option>
                  <option>Thursday</option><option>Friday</option><option>Saturday</option>
                </select>
              </div>
            </div>
            <Field label="Gate code" value={form.gate_code} onChange={(v) => setForm({ ...form, gate_code: v })} testid="customer-gate-code" />
            <Field label="Access notes" value={form.access_notes} onChange={(v) => setForm({ ...form, access_notes: v })} testid="customer-access-notes" />
            <Field label="General notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="customer-notes" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude" type="number" step="any" value={form.lat ?? ""} onChange={(v) => setForm({ ...form, lat: v === "" ? null : Number(v) })} testid="customer-lat" />
              <Field label="Longitude" type="number" step="any" value={form.lng ?? ""} onChange={(v) => setForm({ ...form, lng: v === "" ? null : Number(v) })} testid="customer-lng" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.dog_warning} onChange={(e) => setForm({ ...form, dog_warning: e.target.checked })} data-testid="customer-dog-warning" />
              Dog on premises
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="customer-active" />
              Active customer
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="customer-save">
                {editing === "new" ? "Add customer" : "Save changes"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}{required && <span className="text-rose-600"> *</span>}</label>
      <input
        type={type}
        step={step}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent focus:ring-2 focus:ring-ring focus:outline-none"
      />
    </div>
  );
}

// ---- CSV Import Dialog ----
function parseCSV(text) {
  // Minimal RFC4180-ish CSV parser supporting quoted fields with commas/newlines.
  const rows = [];
  let i = 0, field = "", row = [], inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { row.push(field); field = ""; i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length > 0 && r.some((x) => x && x.trim() !== ""));
}

const COL_MAP = {
  name: ["name", "customer", "customer name", "full name"],
  address: ["address", "street", "addr"],
  postcode: ["postcode", "post code", "postal code", "zip"],
  phone: ["phone", "mobile", "tel", "telephone"],
  email: ["email", "e-mail"],
  price: ["price", "amount", "fee", "cost"],
  frequency: ["frequency", "freq", "schedule"],
  custom_days: ["custom_days", "days", "every_n_days"],
  notes: ["notes", "note", "comments"],
  cleaning_type: ["cleaning_type", "type", "service"],
};

function normaliseFreq(v) {
  const s = (v || "").toString().trim().toLowerCase().replace(/[\s-]/g, "_");
  if (["weekly", "1_week", "7_days"].includes(s)) return "weekly";
  if (["fortnightly", "biweekly", "bi_weekly", "2_weeks", "14_days"].includes(s)) return "fortnightly";
  if (["monthly", "month", "4_weeks"].includes(s)) return "monthly";
  if (["one_off", "oneoff", "once"].includes(s)) return "one_off";
  if (["custom_days", "custom"].includes(s)) return "custom_days";
  return "monthly";
}

function ImportDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); };
    reader.readAsText(f);
  };

  const parse = () => {
    const raw = parseCSV(text);
    if (raw.length < 2) { toast.error("CSV needs a header row + at least 1 data row."); return; }
    const headers = raw[0].map((h) => h.trim().toLowerCase());
    const resolve = (target) => {
      const aliases = COL_MAP[target];
      return headers.findIndex((h) => aliases.includes(h));
    };
    const idx = Object.fromEntries(Object.keys(COL_MAP).map((k) => [k, resolve(k)]));
    if (idx.name === -1) { toast.error("CSV needs a 'name' column."); return; }
    const parsed = raw.slice(1).map((r) => ({
      name: (r[idx.name] || "").trim(),
      address: idx.address > -1 ? (r[idx.address] || "").trim() : "",
      postcode: idx.postcode > -1 ? (r[idx.postcode] || "").trim().toUpperCase() : "",
      phone: idx.phone > -1 ? (r[idx.phone] || "").trim() : "",
      email: idx.email > -1 ? (r[idx.email] || "").trim() : "",
      price: idx.price > -1 ? Number((r[idx.price] || "0").replace(/[^0-9.]/g, "")) || 0 : 0,
      frequency: idx.frequency > -1 ? normaliseFreq(r[idx.frequency]) : "monthly",
      custom_days: idx.custom_days > -1 && r[idx.custom_days] ? Number(r[idx.custom_days]) : null,
      notes: idx.notes > -1 ? (r[idx.notes] || "").trim() : "",
      cleaning_type: idx.cleaning_type > -1 && r[idx.cleaning_type] ? r[idx.cleaning_type].trim() : "exterior",
    }));
    setRows(parsed);
    setResult(null);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customers/import", { rows });
      setResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} customer${data.imported === 1 ? "" : "s"}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row${data.errors.length === 1 ? "" : "s"} skipped`);
      if (data.errors.length === 0) setTimeout(() => { onDone(); }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,address,postcode,phone,email,price,frequency,notes\nMrs Jones,12 Baker St,NW1 6XE,07700 900222,jones@example.com,18.00,monthly,Gate on the right\nMr Smith,45 Oak Rd,SW4 9AA,07700 900333,,22.50,fortnightly,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "windowiqx-customers-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import customers from CSV</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="import-dialog">
          <div className="text-sm text-muted-foreground">
            Upload a CSV with headers including <code className="font-mono">name</code> (required), plus any of: address, postcode, phone, email, price, frequency, custom_days, notes, cleaning_type.
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
              <Upload className="h-4 w-4" />
              <span>Choose file</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
            </label>
            <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="import-download-template">
              <Download className="h-4 w-4" /> Download template
            </button>
          </div>
          <textarea
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Or paste CSV contents here…"
            className="w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent font-mono"
            data-testid="import-csv-text"
          />
          <div className="flex justify-between">
            <button type="button" onClick={parse} disabled={!text} className="rounded-md border border-border px-4 py-2 text-sm disabled:opacity-50" data-testid="import-preview-button">Preview</button>
            <button type="button" onClick={submit} disabled={!rows.length || busy} className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="import-submit">
              {busy ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </button>
          </div>
          {rows.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden" data-testid="import-preview-table">
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Name</th>
                      <th className="px-2 py-1.5 text-left">Postcode</th>
                      <th className="px-2 py-1.5 text-left">Freq</th>
                      <th className="px-2 py-1.5 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1">{r.name}</td>
                        <td className="px-2 py-1 font-mono">{r.postcode}</td>
                        <td className="px-2 py-1 capitalize">{(r.frequency || "").replace("_", " ")}</td>
                        <td className="px-2 py-1 text-right">{formatGBP(r.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 50 && <div className="text-xs text-muted-foreground px-3 py-2">+{rows.length - 50} more (will be imported)</div>}
            </div>
          )}
          {result && (
            <div className="text-sm rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2" data-testid="import-result">
              Imported {result.imported} of {result.total}. {result.errors.length > 0 && `${result.errors.length} errors.`}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
